'use client';

import React, { useEffect } from 'react';
import { motion, useAnimation, useMotionValue, MotionValue, Transition } from 'motion/react';

interface CircularTextProps {
  text: string;
  spinDuration?: number;
  onHover?: 'slowDown' | 'speedUp' | 'pause' | 'goBonkers';
  direction?: 'clockwise' | 'counterclockwise';
  className?: string;
}

const getRotationTransition = (
  duration: number,
  from: number,
  directionMultiplier: 1 | -1,
  loop: boolean = true
) => ({
  from,
  to: from + 360 * directionMultiplier,
  ease: 'linear' as const,
  duration,
  type: 'tween' as const,
  repeat: loop ? Infinity : 0
});

const getTransition = (duration: number, from: number, directionMultiplier: 1 | -1) => ({
  rotate: getRotationTransition(duration, from, directionMultiplier),
  scale: {
    type: 'spring' as const,
    damping: 20,
    stiffness: 300
  }
});

const CircularText: React.FC<CircularTextProps> = ({
  text,
  spinDuration = 20,
  onHover = 'speedUp',
  direction = 'clockwise',
  className = ''
}) => {
  const letters = Array.from(text);
  const controls = useAnimation();
  const rotation: MotionValue<number> = useMotionValue(0);
  const directionMultiplier: 1 | -1 = direction === 'counterclockwise' ? -1 : 1;
  const rotationDelta = 360 * directionMultiplier;

  useEffect(() => {
    const start = rotation.get();
    controls.start({
      rotate: start + rotationDelta,
      scale: 1,
      transition: getTransition(spinDuration, start, directionMultiplier)
    });
  }, [spinDuration, text, onHover, controls, rotation, directionMultiplier, rotationDelta]);

  const handleHoverStart = () => {
    const start = rotation.get();

    if (!onHover) return;

    let transitionConfig: ReturnType<typeof getTransition> | Transition;
    let scaleVal = 1;

    switch (onHover) {
      case 'slowDown':
        transitionConfig = getTransition(spinDuration * 2, start, directionMultiplier);
        break;
      case 'speedUp':
        transitionConfig = getTransition(spinDuration / 4, start, directionMultiplier);
        break;
      case 'pause':
        transitionConfig = {
          rotate: { type: 'spring', damping: 20, stiffness: 300 },
          scale: { type: 'spring', damping: 20, stiffness: 300 }
        };
        break;
      case 'goBonkers':
        transitionConfig = getTransition(spinDuration / 20, start, directionMultiplier);
        scaleVal = 0.8;
        break;
      default:
        transitionConfig = getTransition(spinDuration, start, directionMultiplier);
    }

    controls.start({
      rotate: start + rotationDelta,
      scale: scaleVal,
      transition: transitionConfig
    });
  };

  const handleHoverEnd = () => {
    const start = rotation.get();
    controls.start({
      rotate: start + rotationDelta,
      scale: 1,
      transition: getTransition(spinDuration, start, directionMultiplier)
    });
  };

  return (
    <motion.div
      className={`m-0 mx-auto rounded-full relative font-black text-center cursor-pointer origin-center ${className}`}
      style={{ rotate: rotation }}
      initial={{ rotate: 0 }}
      animate={controls}
      onMouseEnter={handleHoverStart}
      onMouseLeave={handleHoverEnd}
    >
      {letters.map((letter, i) => {
        const rotationDeg = (360 / letters.length) * i;
        const factor = Math.PI / letters.length;
        const x = factor * i;
        const y = factor * i;
        const transform = `rotateZ(${rotationDeg}deg) translate3d(${x}px, ${y}px, 0)`;

        return (
          <span
            key={i}
            className="absolute inline-block inset-0 transition-all duration-500 ease-[cubic-bezier(0,0,0,1)]"
            style={{ transform, WebkitTransform: transform }}
          >
            {letter}
          </span>
        );
      })}
    </motion.div>
  );
};

export default CircularText;
